/**
 * useMmgyClwReviewerSession — MMGY (DV360 / TTD) reviewer session for the CLW.
 *
 * Thin adapter over useReviewerSession (the shared CLW reviewer transport in
 * hooks/useReviewerSession.js). Supplies the MMGY route, the create-payload
 * validation, the auto-start readiness check, and the `final_plan.updated`
 * event reducer (plus the MMGY-only approved_plan_gcs_path on
 * session.finalized). The return shape is unchanged from when this hook was
 * standalone, so CampaignLaunchWorkflow consumes it exactly as before:
 *   { sessionId, messages, suggestions, status, error, connectionError,
 *     approvedMarker, approvedPlanGcsPath, usage, artifactErrors,
 *     interrupting, pendingToolCalls, latestFinalPlan, latestAppliedEdits,
 *     latestPlanGcsPath, lastApplyFailure, lastEditStaged, createSession,
 *     connect, reconnect, sendMessage, interrupt, terminate, newSession,
 *     STATUS }
 *
 * Backend routes (all under the route base):
 *   POST   /api/v2/mmgy-clw-reviewer-sessions
 *   GET    /api/v2/mmgy-clw-reviewer-sessions/:id  (+ /events, /stream)
 *   POST   /api/v2/mmgy-clw-reviewer-sessions/:id/{message,interrupt,terminate}
 */

import { useReviewerSession } from './useReviewerSession';

const ROUTE_BASE = '/api/v2/mmgy-clw-reviewer-sessions';

export function useMmgyClwReviewerSession({ accountId, userId, autoStart = false, createPayload }) {
  return useReviewerSession({
    accountId,
    userId,
    autoStart,
    createPayload,
    routeBase: ROUTE_BASE,
    logName: 'useMmgyClwReviewerSession',
    validateCreatePayload: (p) => {
      if (!p?.destination) return "Missing destination ('dv360' or 'ttd')";
      if (!p?.processingId) return 'Missing processingId';
      if (!p?.finalPlanJson && !p?.finalPlanGcsPath) return 'Missing finalPlanJson or finalPlanGcsPath';
      return null;
    },
    isAutoStartReady: (p) => Boolean(
      p?.destination && p?.processingId && (p?.finalPlanJson || p?.finalPlanGcsPath),
    ),
    autoStartDeps: [
      createPayload?.destination,
      createPayload?.processingId,
      createPayload?.finalPlanJson,
      createPayload?.finalPlanGcsPath,
    ],
    initialPlatformState: {
      latestFinalPlan: null,
      latestAppliedEdits: [],
      latestPlanGcsPath: null,
      approvedPlanGcsPath: null,
    },
    reducePlatformEvent: (data) => {
      // Agent committed an edit batch; backend persisted the new versioned
      // JSON. Surface so the parent can refresh the grid + swap the launch
      // GCS path.
      if (data.type === 'final_plan.updated') {
        const patch = {};
        if (data.final_plan) patch.latestFinalPlan = data.final_plan;
        if (Array.isArray(data.applied_edits)) patch.latestAppliedEdits = data.applied_edits;
        if (data.new_plan_gcs_path) patch.latestPlanGcsPath = data.new_plan_gcs_path;
        return Object.keys(patch).length ? patch : null;
      }
      // MMGY also carries the approved plan path on finalize.
      if (data.type === 'session.finalized' && data.approved_plan_gcs_path) {
        return { approvedPlanGcsPath: data.approved_plan_gcs_path };
      }
      return null;
    },
  });
}
